import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { usePhishPass } from '../../context/PhishPassContext';
import { Plus, Trash2, Pencil, Eye, X, Loader2, FileText } from 'lucide-react';

const blankForm = {
  name: '',
  scenario_type: '',
  subject: '',
  sender_display_name: '',
  sender_email_override: '',
  body_html: '<p>Hi {{EMPLOYEE_NAME}},</p>\n<p>Your message goes here. Include <a href="{{TRACKING_URL}}">a tracked link</a>.</p>',
  red_flags: [],
  landing_message: '',
};

const TemplateList = () => {
  const { templates, fetchTemplates, createTemplate, updateTemplate, deleteTemplate, previewTemplate } = usePhishPass();
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [flagInput, setFlagInput] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => { setLoading(true); await fetchTemplates(); setLoading(false); })(); }, [fetchTemplates]);

  const openNew = () => { setEditing(null); setForm(blankForm); setShowForm(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, scenario_type: t.scenario_type, subject: t.subject,
      sender_display_name: t.sender_display_name, sender_email_override: t.sender_email_override || '',
      body_html: t.body_html, red_flags: t.red_flags || [], landing_message: t.landing_message || '',
    });
    setShowForm(true);
  };

  const addFlag = () => {
    if (!flagInput.trim()) return;
    setForm(prev => ({ ...prev, red_flags: [...prev.red_flags, flagInput.trim()] }));
    setFlagInput('');
  };
  const removeFlag = (i) => setForm(prev => ({ ...prev, red_flags: prev.red_flags.filter((_, idx) => idx !== i) }));

  const handleSave = async () => {
    if (!form.name || !form.subject || !form.sender_display_name || !form.body_html) {
      toast.error('Name, Subject, Sender, and Body are required'); return;
    }
    if (!form.body_html.includes('{{TRACKING_URL}}')) {
      toast.error('Body must include {{TRACKING_URL}} so we can track clicks'); return;
    }
    setSaving(true);
    const payload = { ...form, sender_email_override: form.sender_email_override || null };
    const result = editing ? await updateTemplate(editing.id, payload) : await createTemplate(payload);
    setSaving(false);
    if (result.success) { toast.success(editing ? 'Template updated' : 'Template created'); setShowForm(false); }
    else { toast.error(result.error); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    const r = await deleteTemplate(t.id);
    if (r.success) toast.success('Deleted'); else toast.error(r.error);
  };

  const openPreview = async (t) => {
    const p = await previewTemplate(t.id);
    setPreviewData(p);
    setShowPreview(true);
  };

  return (
    <div className="space-y-4" data-testid="template-list">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Template Library</h2>
          <p className="text-sm text-slate-500">Pre-saved phishing scenarios. Add your own anytime.</p>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-1.5" />New Template
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card key={t.id} data-testid={`template-card-${t.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-blue-100 text-blue-600 flex items-center justify-center"><FileText className="w-4 h-4" /></div>
                    <div>
                      <div className="font-semibold text-sm text-slate-900">{t.name}</div>
                      <Badge variant="outline" className="text-xs mt-0.5">{t.scenario_type}</Badge>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-500 line-clamp-2">Subject: {t.subject}</div>
                <div className="text-xs text-slate-400">From: {t.sender_display_name}</div>
                <div className="flex gap-1 pt-2">
                  <Button variant="outline" size="sm" onClick={() => openPreview(t)} data-testid={`preview-${t.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Preview</Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`edit-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(t)} data-testid={`delete-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!saving) setShowForm(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Template' : 'New Template'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" /></div>
              <div><Label className="text-xs">Scenario Type *</Label><Input value={form.scenario_type} onChange={e => setForm({ ...form, scenario_type: e.target.value })} placeholder="e.g. Credential Phishing" data-testid="tpl-scenario" /></div>
            </div>
            <div><Label className="text-xs">Subject *</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} data-testid="tpl-subject" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Sender Display Name *</Label><Input value={form.sender_display_name} onChange={e => setForm({ ...form, sender_display_name: e.target.value })} placeholder="e.g. IT Helpdesk" data-testid="tpl-sender-name" /></div>
              <div>
                <Label className="text-xs">Sender Email Override (optional)</Label>
                <Input value={form.sender_email_override} onChange={e => setForm({ ...form, sender_email_override: e.target.value })} placeholder="Leave blank to use default" data-testid="tpl-sender-email" />
                <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                  <span>⚠️</span>
                  <span>Must be a Resend-verified domain. Leave blank to use the global default. Unverified domains will cause emails to <b>fail</b> with &quot;not authorized to send&quot;.</span>
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Body HTML * <span className="text-slate-400 font-normal">— must include <code className="bg-slate-100 px-1 rounded">{'{{TRACKING_URL}}'}</code>. Optional: <code className="bg-slate-100 px-1 rounded">{'{{EMPLOYEE_NAME}}'}</code></span></Label>
              <Textarea rows={10} value={form.body_html} onChange={e => setForm({ ...form, body_html: e.target.value })} className="font-mono text-xs" data-testid="tpl-body" />
            </div>
            <div>
              <Label className="text-xs">Red Flags <span className="text-slate-400 font-normal">— shown on the educational landing page</span></Label>
              <div className="flex gap-2">
                <Input value={flagInput} onChange={e => setFlagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFlag())} placeholder="Add a red flag and press Enter" data-testid="tpl-flag-input" />
                <Button type="button" variant="outline" onClick={addFlag}><Plus className="w-4 h-4" /></Button>
              </div>
              <div className="mt-2 space-y-1">
                {form.red_flags.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1 text-xs">
                    <span className="flex-1">{f}</span>
                    <button type="button" onClick={() => removeFlag(i)} className="text-amber-700 hover:text-amber-900"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Landing Page Friendly Message</Label>
              <Textarea rows={2} value={form.landing_message} onChange={e => setForm({ ...form, landing_message: e.target.value })} placeholder="A short reassuring sentence shown on the educational page" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="tpl-save-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Saving…</> : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader><DialogTitle>Email Preview</DialogTitle></DialogHeader>
          {previewData && (
            <div className="space-y-3">
              <div className="bg-slate-50 px-3 py-2 rounded border text-sm">
                <div><b>From:</b> {previewData.sender_display_name} &lt;{previewData.sender_email}&gt;</div>
                <div><b>Subject:</b> {previewData.subject}</div>
              </div>
              <div className="border rounded p-4 text-sm" dangerouslySetInnerHTML={{ __html: previewData.body_html }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplateList;
